import React, { useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  RadioButtonChecked,
  ArrowForward
} from '@mui/icons-material';
import { submitContactForm } from '../../services/api/contact.api';
import CaptchaSection from './CaptchaSection';
import CustomTextarea from './CustomTextarea';
import styles from '../landing/LandingPage.module.scss';

const ContactForm = ({
  title = "Let's Build Around Your Workflow",
  subtitle = "Tell us how your organization works—we'll show you how our platform can match it.",
  showIcon = true,
  onSuccess,
  onError
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organizationName: '',
    organizationType: '',
    trackingDescription: ''
  });

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  // CAPTCHA state
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, operator: '+', answer: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCaptchaChange = (value) => {
    setCaptchaAnswer(value);
    setCaptchaError(false);
  };

  const handleCaptchaGenerate = (newCaptcha) => {
    setCaptcha(newCaptcha);
    setCaptchaAnswer('');
    setCaptchaError(false);
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate CAPTCHA
    if (parseInt(captchaAnswer) !== captcha.answer) {
      setCaptchaError(true);
      showAlert('error', 'Please solve the CAPTCHA correctly.');
      return;
    }

    setLoading(true);

    try {
      const response = await submitContactForm(formData);

      if (response.success) {
        showAlert('success', 'Thank you! Your question has been submitted successfully. We\'ll get back to you within 24 hours.');

        // Reset form
        setFormData({
          name: '',
          email: '',
          organizationName: '',
          organizationType: '',
          trackingDescription: ''
        });

        // Reset CAPTCHA
        setCaptchaAnswer('');
        setCaptchaError(false);

        // Call success callback if provided
        if (onSuccess) {
          onSuccess(response);
        }
      } else {
        throw new Error(response.message || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      showAlert('error', 'Sorry, there was an error submitting your form. Please try again or contact us directly.');

      // Call error callback if provided
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ 
      p: { xs: 3, sm: 4, md: 6 }, 
      mb: { xs: 4, sm: 6, md: 8 }, 
      backgroundColor: '#f8f9fa',
      mx: { xs: 1, sm: 2, md: 0 }
    }}>
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
        <Typography 
          variant="h3" 
          component="h2" 
          className={styles.sectionTitle} 
          sx={{ 
            mb: 2,
            fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
            lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 }
          }}
        >
          {showIcon && (
            <RadioButtonChecked 
              sx={{ 
                fontSize: { xs: 28, sm: 32, md: 40 }, 
                mr: { xs: 1, sm: 1.5, md: 2 }, 
                verticalAlign: 'middle', 
                color: '#2259c9' 
              }} 
            />
          )}
          {title}
        </Typography>
        <Typography 
          variant="h6" 
          className={styles.sectionSubtitle}
          sx={{
            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
            lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
            px: { xs: 1, sm: 2, md: 0 }
          }}
        >
          {subtitle}
        </Typography>
      </Box>

      {alert.show && (
        <Alert
          severity={alert.type}
          sx={{ 
            mb: 3, 
            maxWidth: 800, 
            mx: 'auto',
            fontSize: { xs: '0.85rem', sm: '0.9rem' }
          }}
          onClose={() => setAlert({ show: false, type: '', message: '' })}
        >
          {alert.message}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 800, mx: 'auto' }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={loading}
              variant="outlined"
              inputProps={{ autoFocus: false }}
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  padding: { xs: '12px 14px', sm: '16px 14px' }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={loading}
              variant="outlined"
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  padding: { xs: '12px 14px', sm: '16px 14px' }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Organization Name"
              name="organizationName"
              value={formData.organizationName}
              onChange={handleInputChange}
              disabled={loading}
              variant="outlined"
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  padding: { xs: '12px 14px', sm: '16px 14px' }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Type of Organization"
              name="organizationType"
              value={formData.organizationType}
              onChange={handleInputChange}
              disabled={loading}
              variant="outlined"
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  padding: { xs: '12px 14px', sm: '16px 14px' }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextarea
              label="Tell us what you're hoping to solve?"
              name="trackingDescription"
              value={formData.trackingDescription}
              onChange={handleInputChange}
              disabled={loading}
              required={true}
              minRows={1}
              maxRows={4}
              //placeholder="Describe your tracking needs, workflows, or specific challenges..."
            />
          </Grid>

          {/* CAPTCHA Section */}
          <Grid item xs={12}>
            <CaptchaSection
              captchaAnswer={captchaAnswer}
              onCaptchaChange={handleCaptchaChange}
              captchaError={captchaError}
              loading={loading}
              onCaptchaGenerate={handleCaptchaGenerate}
            />
          </Grid>

          <Grid item xs={12} sx={{ textAlign: 'center', mt: { xs: 1, sm: 2 } }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
              className={styles.primaryCtaButton}
              sx={{ 
                px: { xs: 3, sm: 4, md: 6 }, 
                py: { xs: 1.5, sm: 2 }, 
                fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1.1rem' },
                minWidth: { xs: 160, sm: 180, md: 200 },
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: '280px', sm: 'none' },
                mx: { xs: 'auto', sm: 0 },
                height: { xs: '48px', sm: '56px' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {loading ? 'Submitting...' : "Let's Build Together"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default ContactForm; 